import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import Breadcrumbs from "../../components/seo/Breadcrumbs";
import { getPostBySlug, getRelated, POSTS } from "../../data/blogData";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Clock, BookOpen, Share2, Calendar, Sparkles,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

/* ------------------------------------------------------------------ */
/*  Blog article template (dynamic, reusable)                         */
/*  Route: /blog/:slug                                                */
/* ------------------------------------------------------------------ */

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelated(post.related || []);
  const hasBody = typeof post.body === "string" && post.body.trim().length > 0;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { "@type": "Organization", name: "TRITAL Care®" },
    publisher: {
      "@type": "Organization",
      name: "TRITAL Care®",
      legalName: "TOPTAL Care Inc.",
    },
    mainEntityOfPage: `https://www.tritalcare.com/blog/${post.slug}`,
    articleSection: post.categoryLabel,
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard.");
      }
    } catch {
      /* user cancelled share */
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`blog-post-${post.slug}`}>
      <PageMeta
        title={`${post.title} | TRITAL Care® Resources`}
        description={post.excerpt}
        canonical={`https://www.tritalcare.com/blog/${post.slug}`}
        noIndex={!hasBody}
        schema={articleSchema}
      />
      <PublicNav />

      {/* HERO ------------------------------------------------------ */}
      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-[820px] mx-auto px-5 lg:px-8 pt-14 pb-10 lg:pt-20 lg:pb-14">
          <Breadcrumbs
            className="mb-6"
            trail={[
              { name: "Home", href: "/" },
              { name: "Resources", href: "/blog" },
              { name: post.categoryLabel, href: `/blog?category=${post.category}` },
              { name: post.title },
            ]}
          />

          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/blog?category=${post.category}`}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider hover:bg-trital-navy hover:text-white transition-colors"
            >
              {post.categoryLabel}
            </Link>
            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" strokeWidth={1.8} /> {post.date}
            </span>
            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" strokeWidth={1.8} /> {post.read}
            </span>
          </div>

          <h1 className="mt-5 font-display font-black text-4xl lg:text-[52px] leading-[1.05] tracking-tight text-slate-900 text-balance">
            {post.title}
          </h1>
          <p className="mt-5 text-[17px] lg:text-[18px] text-slate-600 leading-relaxed">
            {post.excerpt}
          </p>

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-[12.5px] font-semibold hover:bg-slate-50"
              data-testid="post-back-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> All resources
            </Link>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-[12.5px] font-semibold hover:bg-slate-50"
              data-testid="post-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Share
            </button>
          </div>
        </div>
      </section>

      {/* BODY ----------------------------------------------------- */}
      <article className="max-w-[760px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
        {hasBody ? (
          <MarkdownBlock source={post.body} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center" data-testid="post-teaser">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">In the editorial queue</span>
            </div>
            <h2 className="mt-5 font-display font-black text-2xl lg:text-[32px] text-slate-900 text-balance">
              Full brief coming soon.
            </h2>
            <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed max-w-[54ch] mx-auto">
              We're finishing fact-checking and clinician review. Subscribe below and we'll send you the
              full brief the morning it publishes.
            </p>
            <Link
              to="/blog#blog-newsletter-form"
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep"
            >
              Subscribe to the brief <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        )}

        {/* Talk to a recruiter inline CTA */}
        <div className="mt-12">
          <SMSRecruiterCard
            variant="default"
            headline={SMS_COPY.contact.headline}
            subtext={SMS_COPY.contact.sub}
          />
        </div>
      </article>

      {/* RELATED -------------------------------------------------- */}
      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200" data-testid="post-related">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
              <div>
                <div className="overline text-trital-red">Keep reading</div>
                <h2 className="mt-3 font-display font-black text-3xl lg:text-[36px] tracking-tight text-slate-900 text-balance leading-tight">
                  Related briefs
                </h2>
              </div>
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-trital-navy hover:gap-2 transition-all"
              >
                All resources <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
                  data-testid={`post-related-${p.slug}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider">
                      {p.categoryLabel}
                    </span>
                    <span className="text-[10.5px] text-slate-500">· {p.date}</span>
                  </div>
                  <h3 className="mt-3 font-display font-bold text-[16px] text-slate-900 leading-snug text-balance">
                    <Link to={`/blog/${p.slug}`} className="hover:text-trital-navy">
                      {p.title}
                    </Link>
                  </h3>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={1.8} /> {p.read}
                    </span>
                    <Link
                      to={`/blog/${p.slug}`}
                      className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-trital-navy"
                    >
                      Read <ArrowUpRight className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MORE FROM TRITAL CARE (cross-nav) ----------------------- */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="post-cross-nav">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85" />
        <div className="absolute inset-0 dot-grid opacity-10" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="overline text-trital-red flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" strokeWidth={1.8} /> More from TRITAL Care
            </div>
            <h3 className="mt-3 font-display font-black text-3xl lg:text-[40px] leading-[1.1] text-white text-balance">
              Insights that become action.
            </h3>
            <p className="mt-4 text-white/80 max-w-[58ch] leading-relaxed">
              Every brief is paired with a TRITAL Care workflow — a capability statement, a checklist, a travel calculator, or a recruiter conversation. Pick the next step that matches what you just read.
            </p>
          </div>
          <ul className="grid sm:grid-cols-1 gap-3">
            {[
              { to: "/employers",            label: "Employer services",       desc: "Staffing, MSP/VMS, credentialing, internal float" },
              { to: "/clinicians",           label: "Clinician pathways",      desc: "Travel · PRN · permanent · fellowships" },
              { to: "/compliance/checklists", label: "Skills checklist hub",    desc: "70+ specialties, recruiter-verified" },
              { to: "/contact",              label: "Talk to a recruiter",     desc: "Reply inside one business hour" },
            ].map((i) => (
              <li key={i.to}>
                <Link
                  to={i.to}
                  className="group flex items-start gap-4 rounded-2xl border border-white/15 bg-white/5 hover:bg-white/10 p-4 transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4 mt-1 text-trital-red shrink-0" strokeWidth={2} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-white group-hover:text-white">{i.label}</div>
                    <div className="mt-0.5 text-[12.5px] text-white/70">{i.desc}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/70 group-hover:text-white" strokeWidth={1.8} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Lightweight Markdown renderer (paragraphs, headings, lists).      */
/*  Not a full Markdown parser — just enough for our in-repo articles.*/
/* ------------------------------------------------------------------ */

function MarkdownBlock({ source }) {
  const blocks = parseBlocks(source);
  return (
    <div className="prose-tritalcare" data-testid="post-body">
      {blocks.map((b, i) => {
        if (b.type === "h2") {
          return (
            <h2
              key={i}
              className="font-display font-black text-2xl lg:text-[28px] tracking-tight text-slate-900 mt-10 mb-4"
            >
              {b.text}
            </h2>
          );
        }
        if (b.type === "h3") {
          return (
            <h3
              key={i}
              className="font-display font-bold text-xl tracking-tight text-slate-900 mt-7 mb-3"
            >
              {b.text}
            </h3>
          );
        }
        if (b.type === "ol") {
          return (
            <ol key={i} className="list-decimal pl-6 mt-4 mb-4 space-y-1.5 text-[15.5px] leading-relaxed text-slate-700">
              {b.items.map((it, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />
              ))}
            </ol>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc pl-6 mt-4 mb-4 space-y-1.5 text-[15.5px] leading-relaxed text-slate-700">
              {b.items.map((it, j) => (
                <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />
              ))}
            </ul>
          );
        }
        return (
          <p
            key={i}
            className="mt-4 text-[15.5px] lg:text-[16.5px] text-slate-700 leading-[1.75]"
            dangerouslySetInnerHTML={{ __html: inline(b.text) }}
          />
        );
      })}
    </div>
  );
}

function parseBlocks(src) {
  const lines = src.split(/\r?\n/);
  const blocks = [];
  let para = [];
  let list = null;
  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: "p", text: para.join(" ").trim() });
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith("### ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h3", text: line.slice(4).trim() });
      continue;
    }
    const olMatch = line.match(/^\d+\.\s+(.*)$/);
    if (olMatch) {
      flushPara();
      if (!list || list.type !== "ol") {
        flushList();
        list = { type: "ol", items: [] };
      }
      list.items.push(olMatch[1]);
      continue;
    }
    if (line.startsWith("- ")) {
      flushPara();
      if (!list || list.type !== "ul") {
        flushList();
        list = { type: "ul", items: [] };
      }
      list.items.push(line.slice(2));
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return blocks;
}

function inline(text) {
  // Escape angle brackets first, then apply bold/italic/inline-code
  const esc = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-slate-900 font-semibold">$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="text-[13.5px] font-mono bg-slate-100 text-trital-navy px-1.5 py-0.5 rounded">$1</code>');
}
